import { PlusIcon } from '@shopify/polaris-icons';
import { PreparationReturn } from '../components/orders/PreparationReturn';
import { ShopifyOrderChanges } from "../components/order-conflicts/ShopifyOrderChanges";
import { ReservationRelease } from '../components/orders/ReservationRelease';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Banner,
  Divider,
  Select,
  Modal,
  TextField,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';
import { DeliveryBooking } from '../components/orders/DeliveryBooking';

const preparationStatuses = [
  { label: 'New', value: 'new' }, { label: 'Backorder', value: 'backorder' },
  { label: 'Allocated', value: 'allocated' }, { label: 'Picking', value: 'picking' },
  { label: 'Picked', value: 'picked' }, { label: 'Packed', value: 'packed' },
  { label: 'Ready', value: 'ready' }, { label: 'On hold', value: 'on_hold' },
];

interface OrderItem {
  id: string;
  sku: string;
  title: string;
  quantity: number;
  allocated_quantity: number;
  quantity_picked?: number;
  quantity_packed?: number;
  quantity_fulfilled?: number;
}

interface FulfillmentItem {
  orderItemId: number;
  quantity: number;
  maxQuantity: number;
  sku: string;
  title: string;
}

interface Order {
  id: string;
  shopify_order_id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  shipping_address?: {
    address1?: string;
    address2?: string;
    city?: string;
    postcode?: string;
    country?: string;
  };
  delivery_date: string | null;
  status: 'new' | 'pending' | 'allocated' | 'picking' | 'dispatched' | 'delivered' | 'cancelled' | 'backorder';
  total_amount?: string;
  items: OrderItem[];
  notes: string | null;
  tracking_token?: string | null;
  created_at: string;
  is_split?: number;
  whenly_is_pickup?: number;
}

export default function OrderDetail() {
  const { orderId } = useParams(); const { currentShop } = useAuth();
  return <OrderDetailPage key={`${currentShop?.id}-${orderId}`} />;
}
function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { fetchApi, error } = useApi();
  const { currentShop, hasPermission } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);

  // Partial fulfillment state
  const [fulfillRequestId,setFulfillRequestId] = useState('');
  const [fulfillModalOpen, setFulfillModalOpen] = useState(false);
  const [fulfillItems, setFulfillItems] = useState<FulfillmentItem[]>([]);
  const [fulfillTracking, setFulfillTracking] = useState({ number: '', company: '', url: '' });
  const [fulfillLoading, setFulfillLoading] = useState(false);

  // Split order state
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [splitPreview, setSplitPreview] = useState<{
    can_split: boolean;
    reason?: string;
    order_number?: string;
    part1_preview?: { order_number: string; description: string; items: Array<{ sku: string; title: string; quantity: number }>; total_qty: number };
    part2_preview?: { order_number: string; description: string; items: Array<{ sku: string; title: string; quantity: number }>; total_qty: number };
  } | null>(null);
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitReason, setSplitReason] = useState('');

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  async function loadOrder() {
    setLoading(true);
    const data = await fetchApi<{ order: Order }>(`/orders/${orderId}`);
    if (data?.order) {
      setOrder(data.order);
      setNewStatus(preparationStatuses.some(option => option.value === data.order.status) ? data.order.status : '');
    }
    setLoading(false);
  }

  const handleStatusUpdate = async () => {
    if (!newStatus || statusSaving || !hasPermission('orders.status.update')) return;
    setStatusSaving(true);
    const result = await fetchApi<{ success: boolean }>(`/orders/${orderId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: newStatus }),
    });
    setStatusSaving(false);
    if (result?.success) loadOrder();
  };

  const handleAllocateStock = async () => {
    const result = await fetchApi<{ success: boolean }>(`/orders/${orderId}/allocate`, {
      method: 'POST',
    });
    if (result?.success) {
      loadOrder();
    }
  };

  // Partial Fulfillment functions
  const openFulfillModal = () => {
    if (!order?.items) return;

    // Build list of unfulfilled items
    const unfulfilledItems: FulfillmentItem[] = order.items
      .filter((item) => {
        const pickedQty = item.quantity_picked ?? item.allocated_quantity ?? 0;
        const fulfilledQty = item.quantity_fulfilled ?? 0;
        return pickedQty > fulfilledQty;
      })
      .map((item) => {
        const pickedQty = item.quantity_picked ?? item.allocated_quantity ?? 0;
        const fulfilledQty = item.quantity_fulfilled ?? 0;
        const maxQty = pickedQty - fulfilledQty;
        return {
          orderItemId: parseInt(item.id),
          quantity: maxQty, // Default to max
          maxQuantity: maxQty,
          sku: item.sku,
          title: item.title,
        };
      });

    setFulfillItems(unfulfilledItems);
    setFulfillTracking({ number: '', company: '', url: '' });
    setFulfillRequestId(crypto.randomUUID());
    setFulfillModalOpen(true);
  };

  const updateFulfillItemQty = (orderItemId: number, qty: string) => {
    setFulfillItems((prev) =>
      prev.map((item) =>
        item.orderItemId === orderItemId
          ? { ...item, quantity: Math.min(Math.max(0, parseInt(qty) || 0), item.maxQuantity) }
          : item
      )
    );
  };

  const handlePartialFulfill = async () => {
    const itemsToFulfill = fulfillItems.filter((item) => item.quantity > 0);
    if (itemsToFulfill.length === 0) {
      return;
    }

    setFulfillLoading(true);

    const result = await fetchApi<{
      success: boolean;
      fulfillmentId?: string;
      fullyFulfilled?: boolean;
      error?: string;
    }>(`/warehouse/orders/${orderId}/fulfill-partial`, {
      method: 'POST',
      body: JSON.stringify({
        request_id: fulfillRequestId,
        items: itemsToFulfill.map((item) => ({
          orderItemId: item.orderItemId,
          quantity: item.quantity,
        })),
        trackingNumber: fulfillTracking.number || undefined,
        trackingCompany: fulfillTracking.company || undefined,
        trackingUrl: fulfillTracking.url || undefined,
      }),
    });

    setFulfillLoading(false);

    if (result?.success) {
      setFulfillModalOpen(false);
      setFulfillItems([]);
      setFulfillTracking({ number: '', company: '', url: '' });
      loadOrder();
    }
  };

  const hasUnfulfilledItems = order?.items?.some((item) => {
    const pickedQty = item.quantity_picked ?? item.allocated_quantity ?? 0;
    const fulfilledQty = item.quantity_fulfilled ?? 0;
    return pickedQty > fulfilledQty;
  });

  // Split order functions
  const openSplitModal = async () => {
    setSplitLoading(true);
    setSplitReason('');
    const preview = await fetchApi<{
      success: boolean;
      can_split: boolean;
      reason?: string;
      order_number?: string;
      part1_preview?: { order_number: string; description: string; items: Array<{ sku: string; title: string; quantity: number }>; total_qty: number };
      part2_preview?: { order_number: string; description: string; items: Array<{ sku: string; title: string; quantity: number }>; total_qty: number };
    }>(`/orders/${orderId}/split-preview`);

    setSplitLoading(false);
    if (preview) {
      setSplitPreview(preview);
      setSplitModalOpen(true);
    }
  };

  const handleSplitOrder = async () => {
    setSplitLoading(true);
    const result = await fetchApi<{
      success: boolean;
      message?: string;
      part1?: { order_id: number; order_number: string };
      part2?: { order_id: number; order_number: string };
      error?: string;
    }>(`/orders/${orderId}/split`, {
      method: 'POST',
      body: JSON.stringify({ reason: splitReason || undefined }),
    });

    setSplitLoading(false);
    if (result?.success) {
      setSplitModalOpen(false);
      setSplitPreview(null);
      // Navigate to the new Part 2 order or refresh
      if (result.part2?.order_id) {
        navigate(`/orders/${result.part2.order_id}`);
      } else {
        loadOrder();
      }
    }
  };

  // Check if order can potentially be split (has both allocated and unallocated items)
  const hasMixedAllocation = order?.items?.some((item) => (item.allocated_quantity ?? 0) > 0) &&
    order?.items?.some((item) => item.quantity > (item.allocated_quantity ?? 0));

  const statusBadge = (status: Order['status']) => {
    const config: Record<Order['status'], { tone: 'success' | 'warning' | 'info' | 'critical' | 'attention'; label: string }> = {
      new: { tone: 'warning', label: 'New' },
      pending: { tone: 'warning', label: 'Pending' },
      allocated: { tone: 'info', label: 'Allocated' },
      picking: { tone: 'attention', label: 'Picking' },
      dispatched: { tone: 'info', label: 'Dispatched' },
      delivered: { tone: 'success', label: 'Delivered' },
      cancelled: { tone: 'critical', label: 'Cancelled' },
      backorder: { tone: 'critical', label: 'Backorder' },
    };
    const { tone, label } = config[status] || { tone: 'info', label: status };
    return <Badge tone={tone}>{label}</Badge>;
  };

  if (loading) {
    return (
      <Page title="Order Details">
        <Layout>
          <Layout.Section>
            <Card>
              <Text as="p">Loading order...</Text>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!order) {
    return (
      <Page title="Order Details">
        <Banner tone="critical">Order not found</Banner>
      </Page>
    );
  }

  const allAllocated = order.items?.length > 0 && order.items.every((item) => item.allocated_quantity >= item.quantity);

  return (
    <Page
      secondaryActions={[{ content: 'Plan shipments', onAction: () => navigate(`/orders/${orderId}/shipments`) }]}
      title={`Order #${order.order_number}`}
      backAction={{ content: 'Orders', onAction: () => navigate('/orders') }}
      titleMetadata={statusBadge(order.status)}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Order Summary */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">Order Summary</Text>
                <Text as="span" tone="subdued">
                  {new Date(order.created_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              </InlineStack>

              <InlineStack gap="800">
                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Customer</Text>
                  <Text as="p" variant="bodyMd">{order.customer_name}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{order.customer_email}</Text>
                </BlockStack>

                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Shipping Address</Text>
                  {order.shipping_address?.address1 ? (
                    <>
                      <Text as="p" variant="bodyMd">{order.shipping_address.address1}</Text>
                      {order.shipping_address.address2 && (
                        <Text as="p" variant="bodySm">{order.shipping_address.address2}</Text>
                      )}
                      <Text as="p" variant="bodySm">
                        {[order.shipping_address.city, order.shipping_address.postcode].filter(Boolean).join(', ')}
                      </Text>
                    </>
                  ) : (
                    <Text as="p" variant="bodySm" tone="subdued">No address provided</Text>
                  )}
                </BlockStack>

                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Delivery Date</Text>
                  {order.delivery_date ? (
                    <Text as="p" variant="headingMd">
                      {new Date(order.delivery_date).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                      })}
                    </Text>
                  ) : (
                    <Badge tone="warning">No date selected</Badge>
                  )}
                  <DeliveryBooking orderId={String(order.id)} onSaved={loadOrder} isPickup={order.whenly_is_pickup === 1} />
                </BlockStack>

                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Total</Text>
                  <Text as="p" variant="headingLg">{order.total_amount ? `€${order.total_amount}` : '-'}</Text>
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section><ShopifyOrderChanges orderId={Number(order.id)} onSaved={loadOrder} /></Layout.Section>

        {/* Order Items */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">Order Items</Text>
                <ReservationRelease orderId={Number(order.id)} onSaved={loadOrder} />
                <PreparationReturn orderId={Number(order.id)} onSaved={loadOrder} />
                {['new', 'pending', 'backorder'].includes(order.status) && (
                  <Button
                    onClick={handleAllocateStock}
                    disabled={allAllocated}
                  >
                    {allAllocated ? 'Fully Allocated' : 'Allocate Stock'}
                  </Button>
                )}
              </InlineStack>

              {order.items?.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'text']}
                  headings={['SKU', 'Product', 'Qty', 'Allocated', 'Fulfilled', 'Status']}
                  rows={order.items.map((item) => {
                    const fulfilledQty = item.quantity_fulfilled ?? 0;
                    const allocatedQty = item.allocated_quantity ?? 0;
                    const pickedQty = item.quantity_picked ?? 0;
                    const packedQty = item.quantity_packed ?? 0;
                    const totalQty = item.quantity ?? 0;
                    const orderStatus = String(order.status);

                    // The line badge reflects the furthest preparation step reached, so it
                    // changes with the preparation status chosen in Actions (WHENLY-76).
                    let statusBadge;
                    if (totalQty > 0 && fulfilledQty >= totalQty) {
                      statusBadge = <Badge tone="success">Fulfilled</Badge>;
                    } else if (fulfilledQty > 0) {
                      statusBadge = <Badge tone="attention">Partially fulfilled</Badge>;
                    } else if (totalQty > 0 && pickedQty >= totalQty && orderStatus === 'ready') {
                      statusBadge = <Badge tone="success">Ready</Badge>;
                    } else if (totalQty > 0 && pickedQty >= totalQty && (orderStatus === 'packed' || packedQty >= totalQty)) {
                      statusBadge = <Badge tone="success">Packed</Badge>;
                    } else if (totalQty > 0 && pickedQty >= totalQty) {
                      statusBadge = <Badge tone="info">Picked</Badge>;
                    } else if (pickedQty > 0) {
                      statusBadge = <Badge tone="attention">Partially picked</Badge>;
                    } else if (totalQty > 0 && allocatedQty >= totalQty) {
                      statusBadge = <Badge tone="info">{orderStatus === 'picking' ? 'Picking' : orderStatus === 'on_hold' ? 'On hold' : 'Allocated'}</Badge>;
                    } else if (allocatedQty > 0) {
                      statusBadge = <Badge tone="warning">Partially allocated</Badge>;
                    } else {
                      statusBadge = <Badge tone="critical">{orderStatus === 'backorder' ? 'Backorder' : 'Unallocated'}</Badge>;
                    }

                    return [
                      item.sku || '-',
                      item.title || '-',
                      totalQty.toString(),
                      allocatedQty.toString(),
                      fulfilledQty.toString(),
                      <InlineStack gap="200" blockAlign="center" key={item.id}>
                        {statusBadge}
                      </InlineStack>,
                    ];
                  })}
                />
              ) : (
                <Text as="p" tone="subdued">No items found</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Actions */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Actions</Text>

              <Select
                label="Preparation status"
                options={[{ label: 'Choose preparation status', value: '' }, ...preparationStatuses]}
                value={newStatus}
                onChange={setNewStatus}
                disabled={statusSaving || !hasPermission('orders.status.update')}
              />
              <Button
                onClick={handleStatusUpdate}
                loading={statusSaving}
                disabled={!newStatus || newStatus === order.status || !hasPermission('orders.status.update')}
              >
                Update preparation status
              </Button>
              <Text as="p" tone="subdued">Dispatch and delivery are recorded through shipment actions. Returns record goods received back.</Text>

              <Divider />

              {order.tracking_token && (
                <Button url={`${import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com'}/track/${encodeURIComponent(order.tracking_token)}`} external>
                  Open current tracking page
                </Button>
              )}

              {currentShop?.domain && (
                <Button url={`https://${currentShop.domain}/admin/orders/${order.shopify_order_id}`} external>
                  View in Shopify
                </Button>
              )}

              {order.status === 'allocated' && (
                <>
                  <Button icon={PlusIcon}
                    variant="primary"
                    onClick={() => navigate(`/pick-lists?order=${order.id}`)}
                  >
                    Create Pick List
                  </Button>

                </>
              )}

              {/* Partial Fulfillment - show for picking/packed orders with unfulfilled items */}
              {(order.status === 'picking' || order.status === 'allocated') && hasUnfulfilledItems && (
                <Button
                  variant="primary"
                  onClick={openFulfillModal}
                >
                  Fulfill Partial
                </Button>
              )}

              {/* Split Order - show when order has mixed allocation (some allocated, some pending) */}
              {hasMixedAllocation && !order.is_split && (
                <>
                  <Divider />
                  <Button
                    onClick={openSplitModal}
                    loading={splitLoading}
                  >
                    Split Order
                  </Button>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Create separate orders for allocated and pending items
                  </Text>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Notes */}
        {order.notes && (
          <Layout.Section variant="oneThird">
            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Notes</Text>
                <Text as="p">{order.notes}</Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}
      </Layout>

      {/* Partial Fulfillment Modal */}
      <Modal
        open={fulfillModalOpen}
        onClose={() => {
          setFulfillModalOpen(false);
          setFulfillItems([]);
          setFulfillTracking({ number: '', company: '', url: '' });
        }}
        title="Fulfill Order Items"
        primaryAction={{
          icon: PlusIcon, content: 'Create Fulfillment',
          onAction: handlePartialFulfill,
          loading: fulfillLoading,
          disabled: fulfillItems.filter((i) => i.quantity > 0).length === 0,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setFulfillModalOpen(false);
              setFulfillItems([]);
              setFulfillTracking({ number: '', company: '', url: '' });
            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p" tone="subdued">
              Select items and quantities to fulfill. This will create a fulfillment in Shopify and notify the customer.
            </Text>

            {/* Items to fulfill */}
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">Items</Text>
              {fulfillItems.map((item) => (
                <InlineStack key={item.orderItemId} gap="400" align="space-between" blockAlign="center">
                  <BlockStack gap="100">
                    <Text as="span" fontWeight="semibold">{item.sku || 'No SKU'}</Text>
                    <Text as="span" tone="subdued">{item.title}</Text>
                  </BlockStack>
                  <div style={{ width: '100px' }}>
                    <TextField
                      label=""
                      labelHidden
                      type="number"
                      value={String(item.quantity)}
                      onChange={(val) => updateFulfillItemQty(item.orderItemId, val)}
                      min={0}
                      max={item.maxQuantity}
                      autoComplete="off"
                      suffix={`/ ${item.maxQuantity}`}
                    />
                  </div>
                </InlineStack>
              ))}
            </BlockStack>

            <Divider />

            {/* Tracking Info */}
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">Tracking Information (Optional)</Text>
              <TextField
                label="Tracking Number"
                value={fulfillTracking.number}
                onChange={(val) => setFulfillTracking((prev) => ({ ...prev, number: val }))}
                autoComplete="off"
              />
              <TextField
                label="Carrier"
                value={fulfillTracking.company}
                onChange={(val) => setFulfillTracking((prev) => ({ ...prev, company: val }))}
                autoComplete="off"
                placeholder="e.g., DHL, FedEx, UPS"
              />
              <TextField
                label="Tracking URL"
                value={fulfillTracking.url}
                onChange={(val) => setFulfillTracking((prev) => ({ ...prev, url: val }))}
                autoComplete="off"
                placeholder="https://..."
              />
            </BlockStack>

            <Banner tone="info">
              The customer will be notified of this fulfillment via email.
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Split Order Modal */}
      <Modal
        open={splitModalOpen}
        onClose={() => {
          setSplitModalOpen(false);
          setSplitPreview(null);
          setSplitReason('');
        }}
        title="Split Order"
        primaryAction={splitPreview?.can_split ? {
          content: 'Split Order',
          onAction: handleSplitOrder,
          loading: splitLoading,
        } : undefined}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setSplitModalOpen(false);
              setSplitPreview(null);
              setSplitReason('');
            },
          },
        ]}
      >
        <Modal.Section>
          {splitPreview && !splitPreview.can_split ? (
            <Banner tone="warning">
              {splitPreview.reason || 'This order cannot be split.'}
            </Banner>
          ) : splitPreview ? (
            <BlockStack gap="400">
              <Text as="p" tone="subdued">
                This will split order <strong>{splitPreview.order_number}</strong> into two separate orders:
              </Text>

              {/* Part 1 Preview */}
              <Card>
                <BlockStack gap="200">
                  <InlineStack gap="200" align="space-between">
                    <Text as="h3" variant="headingMd">{splitPreview.part1_preview?.order_number}</Text>
                    <Badge tone="success">Ready for Delivery</Badge>
                  </InlineStack>
                  <Text as="p" tone="subdued">{splitPreview.part1_preview?.description}</Text>
                  <BlockStack gap="100">
                    {splitPreview.part1_preview?.items.map((item, idx) => (
                      <InlineStack key={idx} gap="200" align="space-between">
                        <Text as="span">{item.sku} - {item.title}</Text>
                        <Text as="span" fontWeight="semibold">×{item.quantity}</Text>
                      </InlineStack>
                    ))}
                  </BlockStack>
                  <Text as="p" fontWeight="semibold">
                    Total: {splitPreview.part1_preview?.total_qty} items
                  </Text>
                </BlockStack>
              </Card>

              {/* Part 2 Preview */}
              <Card>
                <BlockStack gap="200">
                  <InlineStack gap="200" align="space-between">
                    <Text as="h3" variant="headingMd">{splitPreview.part2_preview?.order_number}</Text>
                    <Badge tone="warning">Pending Stock</Badge>
                  </InlineStack>
                  <Text as="p" tone="subdued">{splitPreview.part2_preview?.description}</Text>
                  <BlockStack gap="100">
                    {splitPreview.part2_preview?.items.map((item, idx) => (
                      <InlineStack key={idx} gap="200" align="space-between">
                        <Text as="span">{item.sku} - {item.title}</Text>
                        <Text as="span" fontWeight="semibold">×{item.quantity}</Text>
                      </InlineStack>
                    ))}
                  </BlockStack>
                  <Text as="p" fontWeight="semibold">
                    Total: {splitPreview.part2_preview?.total_qty} items
                  </Text>
                </BlockStack>
              </Card>

              <TextField
                label="Reason for split (optional)"
                value={splitReason}
                onChange={setSplitReason}
                autoComplete="off"
                placeholder="e.g., Customer requested partial delivery"
              />

              <Banner tone="info">
                The allocated items will stay in the original order (renamed to {splitPreview.part1_preview?.order_number}).
                Unallocated items will be moved to a new order ({splitPreview.part2_preview?.order_number}).
              </Banner>
            </BlockStack>
          ) : (
            <Text as="p">Loading preview...</Text>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
